/**
 * Phase 86 Plan 86-05 Task 1 — TOUCH popover.
 *
 * Anchored to a MyTaskRow's TOUCH chip the same way `LogTimePopover` is
 * anchored to the +LOG chip (README "Interactions & behavior" — "TOUCH
 * opens the comment composer focused, since a comment resets
 * `lastActivityAt`"). Mirrors that file's focus-management + Esc + outside
 * click + focus-trap shape rather than inventing a second popover pattern —
 * see `LogTimePopover.tsx`'s own docstring.
 *
 * This is a comment composer, not a bespoke "touch" concept: it mounts the
 * existing `CommentComposer` and lets `useCreateComment` (86-05) own the
 * optimistic move-to-fresh + rollback-on-failure logic against the my-tasks
 * cache. This component only owns: focusing the composer on open, closing on
 * a successful post, and the Esc / outside-click / focus-trap dismissal
 * chrome around it.
 */
import {
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { CommentComposer } from '../comments/CommentComposer';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface MtTouchPopoverProps {
  taskId: number;
  taskName: string;
  projectId: number;
  open: boolean;
  onClose: () => void;
}

export function MtTouchPopover({ taskId, taskName, projectId, open, onClose }: MtTouchPopoverProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Initial focus → the composer's textarea, once the panel mounts. The
  // composer has no ref/autoFocus prop of its own (not a file this plan
  // touches), so we locate it by the same accessible name the composer's
  // own tests already assert (`aria-label="Comment body"`).
  useEffect(() => {
    if (!open) return;
    const raf = window.requestAnimationFrame(() => {
      const textarea = containerRef.current?.querySelector<HTMLTextAreaElement>(
        'textarea[aria-label="Comment body"]',
      );
      textarea?.focus();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [open]);

  // Document-level Escape listener + outside-click close — mirrors
  // LogTimePopover.tsx.
  useEffect(() => {
    if (!open) return;

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }

    function handleMouseDown(e: MouseEvent) {
      const container = containerRef.current;
      if (!container) return;
      if (!container.contains(e.target as Node)) onClose();
    }

    document.addEventListener('keydown', handleKey, true);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('keydown', handleKey, true);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [open, onClose]);

  // Focus trap — Tab/Shift+Tab wraps within the popover.
  const handlePanelKeyDown = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const panel = containerRef.current;
    if (!panel) return;
    const focusables = Array.from(
      panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => !el.hasAttribute('disabled'));
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  if (!open) return null;

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-label="Touch task"
      onKeyDown={handlePanelKeyDown}
      // Belt-and-suspenders alongside MyTaskRow's own open-state guard
      // (README "Action chip click — stopPropagation" / 86-CONTEXT "clicks
      // stopPropagation so they do not trigger the row's navigation"):
      // nothing inside this panel — including the composer's own buttons —
      // should ever bubble a click up to the row.
      onClick={(e) => e.stopPropagation()}
      data-testid="mt-touch-popover"
      className="absolute right-0 top-full mt-1 z-40 w-80 bg-ms-surface border border-ms-border rounded-lg shadow-xl p-3"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[11px] font-semibold text-ms-text-strong">Touch</span>
        <span className="text-[11px] text-ms-text-muted truncate" title={taskName}>
          {taskName}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="ml-auto text-ms-text-muted hover:text-ms-text-strong text-[11px]"
          aria-label="Close popover"
          data-testid="mt-touch-popover-close"
        >
          Esc
        </button>
      </div>
      <CommentComposer
        taskId={taskId}
        projectId={projectId}
        audience="internal"
        placeholder="What did you just do? (this counts as activity and moves the row to Fresh)"
        onPosted={onClose}
      />
    </div>
  );
}
